import type { ChatMessageContent } from "schemas/generated/chatMessage";

import React from "react";

import ChangeBlock from "@/features/react-editor/components/chat/chat-elements/ChangeBlock";
import { useMessages } from "@/features/react-editor/components/chat/store/chatStore";
import Button from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import {
  chatBuildErrorItemSchema,
  chatProductItemSchema,
} from "schemas/chatMessage";

import { ChatTextRenderer } from "./ChatTextRenderer";
import { MarkdownRenderer } from "./MarkdownRenderer";

export interface Message {
  id: string;
  role: "user" | "assistant" | (string & {});
  content: ChatMessageContent;
  createdAt?: Date;
}

interface ChatMessageProps extends Message {
  showTimeStamp?: boolean;
  animation?: Animation;
  className?: string;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  createdAt,
  showTimeStamp = false,
  className,
}) => {
  const isUser = role === "user";

  return (
    <div
      className={twMerge(
        "flex flex-col gap-2",
        isUser ? "items-end" : "items-start",
      )}
    >
      <MessageContent content={content} role={role} className={className} />

      {showTimeStamp && createdAt && role !== "tool" && (
        <MessageTimestamp createdAt={createdAt} />
      )}
    </div>
  );
};

const MessageContent: React.FC<{
  content: ChatMessageContent;
  role: string;
  className?: string;
}> = ({ content, role, className }) => {
  return (
    <div className="flex flex-wrap gap-3 items-end">
      {exhaustiveSwitch(content)({
        string: ({ content }) => (
          <StringMessageContent
            content={content}
            role={role}
            className={className}
          />
        ),
        array: ({ content }) => (
          <ArrayMessageContent
            content={content}
            role={role}
            className={className}
          />
        ),
      })}
    </div>
  );
};

const StringMessageContent: React.FC<{
  content: string;
  role: string;
  className?: string;
}> = ({ content, role, className }) => (
  <div className="">
    <div
      className={twMerge(
        "text-[14px] relative break-words rounded-lg p-3 text-default ml-10",
        role === "user" && "bg-sheet",
        className,
      )}
    >
      <ChatTextRenderer content={content} />
    </div>
  </div>
);

// This is for when the content looks something like this:
// {"type": "array", "content": [{"type": "text", "text": "Thank you for letting me know. I'll revert the last change to remove the ContactUs component from the bottom of your page and restore it
//                                            to its previous state.\n\nLet me do that for you now."},
//                               {"type": "tool-call", "args": {},  "toolName": "codeChangeTool", "toolCallId": "call_hVh5Cke5dczCrRr6uWqYWxGV"}]}
const ArrayMessageContent: React.FC<{
  content: Extract<ChatMessageContent, { type: "array" }>["content"];
  role: string;
  className?: string;
}> = ({ content, role, className }) => (
  <div className="flex flex-wrap gap-3">
    {content.map((item, index) => (
      <div className="flex gap-3" key={`${item.type}-${index}`}>
        <MessageItem item={item} role={role} className={className} />
      </div>
    ))}
  </div>
);

const MessageItem: React.FC<{
  item: Extract<ChatMessageContent, { type: "array" }>["content"][number];
  role: string;
  className?: string;
}> = ({ item, role, className }) => {
  if (item.type === "text") {
    return <TextMessageItem item={item} role={role} className={className} />;
  }

  if (item.type === "tool-call") {
    return <ToolCallMessageItem item={item} className={className} />;
  }

  if (item.type === "image") {
    return <ImageMessageItem item={item} />;
  }

  return null;
};

// Text message item renderer
const TextMessageItem: React.FC<{
  item: Extract<ChatMessageContent["content"][number], { type: "text" }>;
  role: string;
  className?: string;
}> = ({ item, role, className }) => {
  if (item.isHidden) {
    return null;
  }

  if (item.contentType === "product") {
    return <ProductRenderer text={item.text} />;
  }

  if (item.contentType === "buildError") {
    return <BuildErrorRenderer text={item.text} />;
  }

  if (item.contentType === "revert") {
    return <RevertRenderer text={item.text} />;
  }

  // Default text content (contentType === "text" or no contentType)
  if (item.contentType === "text" || !item.contentType) {
    return (
      <div
        className={twMerge(
          "text-[14px] relative break-words rounded-lg p-3 text-default",
          role === "user" && "bg-sheet",
          className,
        )}
      >
        <MarkdownRenderer>{item.text}</MarkdownRenderer>
      </div>
    );
  }

  return null;
};

// Tool call message item renderer
const ToolCallMessageItem: React.FC<{
  item: Extract<ChatMessageContent["content"][number], { type: "tool-call" }>;
  className?: string;
}> = ({ item, className }) => {
  const allMessages = useMessages();
  return (
    <div className={twMerge("text-[14px] pl-3", className)}>
      <ToolCallRenderer
        toolName={item.toolName}
        toolResult={findToolResult(item.toolCallId, allMessages)}
      />
    </div>
  );
};

// Image message item renderer
const ImageMessageItem: React.FC<{
  item: Extract<ChatMessageContent["content"][number], { type: "image" }>;
}> = ({ item }) => {
  if (item.isHidden) {
    return null;
  }

  return (
    <div key={item.id} className="w-[75px] h-[75px] overflow-hidden rounded-lg">
      <img
        src={item.image}
        alt={item.id}
        className="w-full h-full object-cover"
      />
    </div>
  );
};

// Timestamp renderer
const MessageTimestamp: React.FC<{
  createdAt: Date;
}> = ({ createdAt }) => {
  const formattedTime = createdAt.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <time
      dateTime={createdAt.toISOString()}
      className="mt-1 block px-1 text-xs opacity-50"
    >
      {formattedTime}
    </time>
  );
};

const ProductRenderer = ({ text }: { text: string }) => {
  const product = chatProductItemSchema.parse(JSON.parse(text));
  return (
    <div className="flex gap-3 border border-border rounded-lg p-1 w-[200px]">
      <div className="flex-shrink-0">
        <img
          src={product.image ?? ""}
          alt={product.title}
          className="w-12 h-12 object-cover rounded-md"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-default truncate">
          {product.title}
        </div>
        <div className="text-xs text-muted mt-1 line-clamp-2">
          {product.description}
        </div>
      </div>
    </div>
  );
};

const BuildErrorRenderer = ({ text }: { text: string }) => {
  const validatedText = chatBuildErrorItemSchema.safeParse(JSON.parse(text));
  if (!validatedText.success) {
    return null;
  }

  const buildError = validatedText.data;

  return (
    <div
      className={twMerge(
        "flex flex-col gap-1 rounded-xl p-3 bg-button-danger-link-hover",
      )}
    >
      <div className="font-semibold text-base">Welp, that didn't work</div>
      <div className="text-sm text-muted">
        We've reverted back to the previous version. Try again or try a
        different prompt!
      </div>

      <div className="flex gap-2">
        <Button
          isLoading={buildError.isReverting}
          disabled={buildError.isReverting}
          variant="secondary"
          size="sm"
          // TODO (Bobby: 2025-07-11): Implement try again logic
          onClick={() => {}}
        >
          Try Again
        </Button>
      </div>
    </div>
  );
};

const RevertRenderer = ({ text }: { text: string }) => {
  return (
    <div className="my-2">
      Revert{" "}
      <span className="font-medium ml-1.5 text-base text-default border border-border rounded-full py-1 px-2">
        {text}
      </span>
    </div>
  );
};

type ToolResultType = Extract<
  ChatMessageContent["content"][number],
  { type: "tool-result" }
>;

const ToolCallRenderer = ({
  toolName,
  toolResult,
}: {
  toolName: string;
  toolResult: ToolResultType | null;
}) => {
  switch (toolName) {
    case "codeChangeTool":
      return (
        <ChangeBlock
          message={toolResult ? "Changes Applied" : "Applying Changes..."}
          isLoading={!toolResult}
          hasError={toolResult?.result.success === false}
          appliedDiffs={toolResult?.result.appliedDiffs}
          fileVersionId={toolResult?.result.fileVersionId}
          fileVersionSummary={toolResult?.result.fileVersionSummary}
        />
      );
    default:
      return null;
  }
};

const findToolResult = (
  toolCallId: string,
  allMessages: Message[],
): ToolResultType | null => {
  const toolResultMessage = allMessages.find((message) => {
    if (message.role !== "tool") {
      return false;
    }

    if (message.content.type === "array") {
      return message.content.content.some(
        (item) => item.type === "tool-result" && item.toolCallId === toolCallId,
      );
    }

    return false;
  });

  return (
    toolResultMessage?.content.type === "array"
      ? toolResultMessage.content.content.find(
          (item) =>
            item.type === "tool-result" && item.toolCallId === toolCallId,
        )
      : null
  ) as ToolResultType | null;
};
